import { Stack, SvgIcon, Typography } from '@mui/material';
import type React from 'react';

import { borderRadius, boxShadows, palette, spaces } from '../../../../assets/styles/themes/tokens';
import getMuiIcon, { type IconName } from '../../../../utils/getMuiIcon';
import FeatureStatusChip from '../../../cards/MuiCard/MuiCardBase/FeatureStatusChip';

const AIFeatureCard: React.FC<{
	title: string;
	icon: IconName;
	description: string;
	onClick: () => void;
	disabled?: boolean;
	chip?: 'new' | 'beta';
	testId?: string;
	emphasized?: boolean;
	heightPx?: number;
}> = ({
	title,
	icon,
	description,
	disabled,
	chip,
	onClick,
	testId = 'ai-assistant-template-prompt-card',
	emphasized,
	heightPx,
}) => {
	return (
		<Stack
			data-testid={testId}
			gap={spaces.sm}
			onClick={disabled ? undefined : onClick}
			sx={{
				backgroundColor: palette.background.paperElevation2,
				padding: spaces.sm,
				borderRadius: borderRadius.md,
				height: heightPx ?? (emphasized ? 200 : 116),
				boxShadow: boxShadows.elevation1,
				...(emphasized && {
					border: `1px solid ${palette.elevation.outlined}`,
				}),
				userSelect: 'none',
				cursor: 'pointer',
				transition: 'all 0.3s ease',
				'&:hover': {
					boxShadow: boxShadows.elevation3,
				},
				'&:focus': {
					border: `1px solid ${palette.primary.main}`,
				},
				...(disabled && {
					opacity: 0.5,
					cursor: 'default',
				}),
			}}
		>
			<Stack direction="row" gap={spaces.xs} justifyContent="space-between">
				<Typography
					variant={emphasized ? 'subtitle2' : 'subtitle3'}
					sx={{
						color: palette.text.primary,
						hyphens: 'auto',
						overflow: 'hidden',
						textOverflow: 'ellipsis',
						...(emphasized ? { fontWeight: 'bold' } : { height: '32px' }),
					}}
				>
					{title}
				</Typography>
				{chip ? (
					<FeatureStatusChip
						size="small"
						status={chip}
						sx={{ position: 'static', top: 'unset', left: 'unset' }}
					/>
				) : (
					<Stack
						data-testid="ai-feature-card-icon"
						justifyContent="center"
						alignItems="center"
						sx={{
							backgroundColor: palette.primary.contrast,
							color: palette.primary.main,
							padding: spaces.xs,
							borderRadius: borderRadius.full,
							...(emphasized && { alignSelf: 'flex-start', flexShrink: 0 }),
						}}
					>
						<SvgIcon component={getMuiIcon(icon)} sx={{ width: 16, height: 16 }} viewBox="0 0 16 16" />
					</Stack>
				)}
			</Stack>
			<Typography
				variant="caption"
				sx={{
					color: palette.text.disabled,
					hyphens: 'auto',
					overflow: 'hidden',
					textOverflow: 'ellipsis',
				}}
			>
				{description}
			</Typography>
		</Stack>
	);
};

export default AIFeatureCard;
